import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";
import SubscriptionService from "app/services/SubscriptionServices";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import TopNavBar from "app/layout/TopNavBar";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Static package data
const availablePackages = [
    {
        name_si: "Family",
        name_en: "Family",
        unit_price: 120,
        unites_per_package: 10,
        price: 1200,
        validity: 366,
        data_renention: 730,
        description_si: "ඔබේ පවුලේ සියලුම සාමාජිකයින්ටම වසර 2ක් ඇතුළත භාවිත කළ හැකි Family පැකේජය, රු. 1,200කට.",
        description_en: "For your whole family—10 members, 2 years, just Rs. 1,200.",
        is_active: true,
        package_id: "100-family"
    },
    {
        name_si: "Basic",
        name_en: "Basic",
        unit_price: 40,
        unites_per_package: 50,
        price: 2000,
        validity: 183,
        data_renention: 730,
        description_si: "Basic පැකේජය, මාස 6ක් ඇතුළත ඒකක 50ක්, රු. 2,000කට.",
        description_en: "Basic Package—50 units, 6 months, Rs. 2,000.",
        is_active: true,
        package_id: "200-basic"
    },
    {
        name_si: "Lite",
        name_en: "Lite",
        unit_price: 36,
        unites_per_package: 100,
        price: 3600,
        validity: 366,
        data_renention: 730,
        description_si: "Lite පැකේජය, වසර 1ක් ඇතුළත ඒකක 100ක්, රු. 3,600.",
        description_en: "Lite Package—100 units, 1 year, Rs. 3,600.",
        is_active: true,
        package_id: "300-lite"
    },
    {
        name_si: "Pro",
        name_en: "Pro",
        unit_price: 30,
        unites_per_package: 300,
        price: 9000,
        validity: 365,
        data_renention: 730,
        description_si: "Pro පැකේජය, වසර 1ක් ඇතුළත ඒකක 300ක්, රු. 9,000.",
        description_en: "Pro Package—300 units, 1 year, Rs. 9,000.",
        is_active: true,
        package_id: "400-pro"
    }
];

// Helper to decode JWT
function decodeJwt(token) {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
}

function AccountSubscriptionsTable({ }) {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const googleUser = useMemo(() => {
        const googleToken = window.localStorage.getItem('googleToken');
        return googleToken ? decodeJwt(googleToken) : null;
    }, []);

    const tableStyles = useMemo(() => ({
        table: {
            width: "100%",
            minWidth: 700,
            borderCollapse: "collapse",
            margin: "24px 0",
            fontSize: "0.95rem",
            background: darkMode ? "#202940" : "#ffffff",
            borderRadius: "12px",
            overflow: "hidden",
            border: darkMode ? "1px solid #34445a" : "1px solid #e0e0e0"
        },
        th: {
            border: darkMode ? "1px solid #34445a" : "1px solid #e0e0e0",
            padding: "12px 14px",
            background: darkMode ? "#1a2035" : "#f0f2f5",
            color: darkMode ? "#ffffff" : "#344767",
            fontWeight: "bold",
            textAlign: "left"
        },
        td: {
            border: darkMode ? "1px solid #34445a" : "1px solid #e0e0e0",
            padding: "12px 14px",
            color: darkMode ? "#e0e0e0" : "#495057"
        },
        trAlt: {
            background: darkMode ? "#1b2330" : "#fafafa"
        }
    }), [darkMode]);

    const [subscriptions, setSubscriptions] = useState([]);
    const subscriptionService = SubscriptionService();

    const fetchData = async () => {
        try {
            const subs = await subscriptionService.getSubscriptions();
            setSubscriptions(subs || []);
        } catch (err) {
            setSubscriptions([]);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const getPackageInfo = (pkg) => {
        let pkgKey = "family";
        if (pkg.package_id.includes("basic")) pkgKey = "basic";
        else if (pkg.package_id.includes("lite")) pkgKey = "lite";
        else if (pkg.package_id.includes("pro")) pkgKey = "pro";

        return {
            name: t(`account.packages.${pkgKey}.name`, { defaultValue: pkg.name_en }),
            desc: t(`account.packages.${pkgKey}.desc`, { defaultValue: pkg.description_en })
        };
    };

    return (
        <DashboardLayout>
            <TopNavBar />
            <style>
                {`
                .account-sub-profile {
                    display: flex;
                    align-items: center;
                    margin-bottom: 24px;
                    padding: 16px;
                    border-radius: 12px;
                    background: ${darkMode ? "#202940" : "#ffffff"};
                    border: 1px solid ${darkMode ? "#34445a" : "#e0e0e0"};
                }
                .account-sub-profile-img {
                    width: 60px;
                    height: 60px;
                    border-radius: 50%;
                    margin-right: 16px;
                    border: 2px solid #2196f3;
                }
                .account-sub-contact-list {
                    list-style: none;
                    padding: 0;
                    text-align: center;
                    max-width: 600px;
                    margin: 0 auto;
                }
                .account-sub-contact-list li {
                    margin-bottom: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 8px;
                }
                .account-sub-buy-section {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    margin-top: 32px;
                    padding: 24px 16px;
                    border-radius: 12px;
                    background: ${darkMode ? "#202940" : "#ffffff"};
                    border: 1px solid ${darkMode ? "#34445a" : "#e0e0e0"};
                }
                .account-sub-feature-item {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 8px;
                    font-size: 0.95rem;
                    color: ${darkMode ? "#d0d7de" : "#495057"};
                }
            `}
            </style>

            {googleUser && (
                <div className="account-sub-profile">
                    <img src={googleUser.picture} alt="Profile" className="account-sub-profile-img" />
                    <div>
                        <div style={{ fontWeight: "bold", fontSize: "1.1em", color: darkMode ? "#ffffff" : "#344767" }}>
                            {googleUser.name}
                        </div>
                        <div style={{ color: darkMode ? "#a0aec0" : "#6c757d" }}>
                            {googleUser.email}
                        </div>
                    </div>
                </div>
            )}

            <Card sx={{ p: 3, mb: 3 }}>
                <MDTypography variant="h4" fontWeight="bold" color={darkMode ? "white" : "dark"} mb={0.5}>
                    {t("account.title")}
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={2}>
                    {t("account.subtitle")}
                </MDTypography>

                <div style={{ width: "100%", overflowX: "auto" }}>
                    <table style={tableStyles.table}>
                        <thead>
                            <tr>
                                <th style={tableStyles.th}>{t("account.thPackage")}</th>
                                <th style={tableStyles.th}>{t("account.thPoints")}</th>
                                <th style={tableStyles.th}>{t("account.thBalance")}</th>
                                <th style={tableStyles.th}>{t("account.thStartDate")}</th>
                                <th style={tableStyles.th}>{t("account.thEndDate")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subscriptions.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ ...tableStyles.td, textAlign: "center", padding: "20px" }}>
                                        {t("account.noSubscriptions")}
                                    </td>
                                </tr>
                            ) : (
                                subscriptions.map((sub, idx) => (
                                    <tr key={idx} style={idx % 2 ? tableStyles.trAlt : {}}>
                                        <td style={tableStyles.td}><strong>{sub.package_name}</strong></td>
                                        <td style={tableStyles.td}>
                                            {(sub.price / sub.unit_price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </td>
                                        <td
                                            style={{
                                                ...tableStyles.td,
                                                background: sub.balance === 0
                                                    ? (darkMode ? "rgba(244, 67, 54, 0.2)" : "#ffdddd")
                                                    : (darkMode ? "rgba(76, 175, 80, 0.2)" : "#ddffdd"),
                                                color: sub.balance === 0
                                                    ? (darkMode ? "#ff867c" : "#b20000")
                                                    : (darkMode ? "#81c784" : "#2e7d32"),
                                                fontWeight: "bold"
                                            }}
                                        >
                                            {(sub.balance / sub.unit_price).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                        </td>
                                        <td style={tableStyles.td}>{sub.start_date ? sub.start_date.substring(0, 10) : "-"}</td>
                                        <td style={tableStyles.td}>{sub.end_date ? sub.end_date.substring(0, 10) : "-"}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <div className="account-sub-buy-section">
                <MDTypography variant="h4" fontWeight="bold" textAlign="center" mb={1} color={darkMode ? "white" : "dark"}>
                    {t("account.buyNewTitle")}
                </MDTypography>
                <MDTypography variant="h5" color="primary" textAlign="center" mb={2}>
                    {t("account.contactUs")}
                </MDTypography>

                <ul className="account-sub-contact-list">
                    <li>
                        <strong style={{ color: darkMode ? "#ffffff" : "#344767" }}>
                            {t("account.colomboLocation")}:
                        </strong>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: "middle" }}>
                            <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.6 17.6 0 0 0 4.168 6.608 17.6 17.6 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.68.68 0 0 0-.58-.122l-2.19.547a1.75 1.75 0 0 1-1.657-.459L5.482 8.062a1.75 1.75 0 0 1-.46-1.657l.548-2.19a.68.68 0 0 0-.122-.58zM1.884.511a1.745 1.745 0 0 1 2.612.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.68.68 0 0 0 .178.643l2.457 2.457a.68.68 0 0 0 .644.178l2.189-.547a1.75 1.75 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.6 18.6 0 0 1-7.01-4.42 18.6 18.6 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877z" />
                        </svg>
                        <a style={{ color: darkMode ? "#90caf9" : "#1976d2", fontWeight: 600 }} href="tel:+94765243543">
                            +94 765 243 543
                        </a>
                        <span style={{ color: darkMode ? "#a0aec0" : "#6c757d" }}>|</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#25D366" viewBox="0 0 16 16" style={{ verticalAlign: "middle" }}>
                            <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                        </svg>
                        <a style={{ color: "#25D366", fontWeight: 600 }} href="https://wa.me/94765243543" target="_blank" rel="noopener noreferrer">
                            WhatsApp
                        </a>
                    </li>
                </ul>

                <MDTypography variant="h5" color="info" fontWeight="bold" mt={1} mb={3}>
                    {t("account.startingPrice")}
                </MDTypography>

                <div style={{ width: "100%", maxWidth: 700 }}>
                    <MDTypography variant="h4" fontWeight="bold" textAlign="center" mb={2} color={darkMode ? "white" : "dark"}>
                        {t("account.availablePackages")}
                    </MDTypography>

                    {availablePackages.map(pkg => {
                        const info = getPackageInfo(pkg);
                        return (
                            <MDBox
                                key={pkg.package_id}
                                sx={{
                                    border: darkMode ? "1px solid #34445a" : "1px solid #e0e0e0",
                                    borderRadius: "12px",
                                    mb: 2.5,
                                    p: 2.5,
                                    background: darkMode ? "#1a2035" : "#f9f9ff",
                                    transition: "transform 0.2s, box-shadow 0.2s",
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow: 3
                                    }
                                }}
                            >
                                <div style={{ fontWeight: 700, fontSize: "1.15rem", marginBottom: 6, color: darkMode ? "#ffffff" : "#344767" }}>
                                    {info.name}
                                </div>
                                <div style={{ color: darkMode ? "#90caf9" : "#0d47a1", fontWeight: 600, fontSize: "0.95rem", marginBottom: 8 }}>
                                    {t("account.priceLabel", { price: pkg.price.toLocaleString() })} | {t("account.pointsLabel", { count: pkg.unites_per_package })}
                                </div>
                                <div style={{ color: darkMode ? "#cbd5e1" : "#495057", fontSize: "0.925rem", lineHeight: 1.5 }}>
                                    {info.desc}
                                </div>
                            </MDBox>
                        );
                    })}
                </div>

                <MDBox mt={3} width="100%" maxWidth={600} display="flex" flexDirection="column" alignItems="center">
                    <div className="account-sub-feature-item">
                        <span style={{ color: "#4caf50", fontWeight: "bold" }}>✓</span> {t("account.features.freeToStart")}
                    </div>
                    <div className="account-sub-feature-item">
                        <span style={{ color: "#4caf50", fontWeight: "bold" }}>✓</span> {t("account.features.payAsYouGo")}
                    </div>
                    <div className="account-sub-feature-item">
                        <span style={{ color: "#4caf50", fontWeight: "bold" }}>✓</span> {t("account.features.access247")}
                    </div>
                    <div className="account-sub-feature-item">
                        <span style={{ color: "#4caf50", fontWeight: "bold" }}>✓</span> {t("account.features.worldwide")}
                    </div>
                    <div className="account-sub-feature-item" style={{ textAlign: "center" }}>
                        <span style={{ color: "#4caf50", fontWeight: "bold" }}>✓</span> {t("account.features.crossDevice")}
                    </div>
                </MDBox>
            </div>
        </DashboardLayout>
    );
};

export default AccountSubscriptionsTable;
